export const HtmlSizerStrategy = {
  pctWidth: 0,
  pctWidthPxHeight: 1,
};

export class HtmlAspectRatioSizer {
  /**
   * @param {SizerStragety} sizerStrategy Determines whether we only set the width, of if we set both width and height.
   * @param {bool} alwaysUseWidthPct If false, then the height might be set to 100% in some cases. If true, then we'll
   * instead convert from 100% to the corresponding px value.
   */
  constructor(sizerStrategy, alwaysUseWidthPct) {
    this.sizerStrategy = sizerStrategy;
    this.alwaysUseWidthPct = alwaysUseWidthPct;
  }

  alignCenter(child, usePreCalculatedSizes) {
    let childLength = usePreCalculatedSizes ? this.childWidth : child.clientWidth;
    let diff = child.parentNode.clientWidth - childLength;
    child.style.left = (diff / 2) + 'px';

    childLength = usePreCalculatedSizes ? this.childHeight : child.clientHeight;
    diff = child.parentNode.clientHeight - childLength;
    child.style.top = diff > 1 ? (diff / 2) + 'px' : 0;
  }

  changeZoom(child, currentZoomPct, delta) {
    if (!child.parentNode) {
      // We have a case there the targetElement doesn't have a parent. Not sure why yet. 11/22/2021.
      return currentZoomPct;
    }
    const newZoomPct = currentZoomPct + delta;
    this.childWidth = child.parentNode.clientWidth * newZoomPct / 100;
    const childWHRatio = child.clientWidth / child.clientHeight;
    this.childHeight = this.childWidth / childWHRatio;
    return newZoomPct;
  }

  /**
   * If width/height are specified, use them for the calculation. Otherwise, if the element has
   * a NaturalWidth/-height, use them. Otherwise, use the clientWidth/-Height.
   * @param {Element} element The element whose w/h ratio we are calculating
   * @param {int} width A user specified width for the element.
   * @param {int} height A user specified height for the element.
   */
  static getWidthHeightRatio(element, width, height) {
    if (width && height) {
      return width / height;
    } else if (element.naturalWidth) {
      return element.naturalWidth / element.naturalHeight;
    }
    return element.clientWidth / element.clientHeight;
  }

  static hasHigherWHRatioThanParent(child, childWHRatio) {
    // If childWHRatio is specified, use that. Otherwise try to get the ratio from the child element:
    childWHRatio = childWHRatio || HtmlAspectRatioSizer.getWidthHeightRatio(child);
    const parent = child.parentNode;
    const parentWHRatio = parent.clientWidth / parent.clientHeight;
    return childWHRatio > parentWHRatio;
  }

  /**
   * Contain the entire child element within the parent by setting both a width and height.
   * @param {Element} child The html element which will be sized.
   * @param {int} logicalWidth For calculating the width/height ratio to be used for sizing the child element.
   * @param {int} logicalHeight For calculating the width/height ratio to be used for sizing the child element.
   * @param {double} childWHRatio If provided, this is the w/h ratio used and logicalWidth/-Height will be ignored.
   * @returns { width, height, cssWidth, cssHeight } Returns an object with width/height integers and
   * css width/height styles to be set on the child element (must be done by the caller).
   */
  containUsingWidthAndHeight(child, logicalWidth, logicalHeight, childWHRatio) {
    // If childWHRatio is specified, use that. Otherwise try to get the ratio from the child element:
    childWHRatio = childWHRatio || HtmlAspectRatioSizer.getWidthHeightRatio(child, logicalWidth, logicalHeight);
    const parent = child.parentNode;
    const parentWidth = parent.clientWidth;
    //const parentHeight = parent.clientHeight;
    let tmpHeight = 0;
    let tmpParent = parent;
    while (!tmpHeight && tmpParent)
    {
      tmpHeight = tmpParent.clientHeight;
      tmpParent = tmpParent.parentNode;
    }
    const parentHeight = tmpHeight;
    const parentWHRatio = parentWidth / parentHeight;
    const result = {};
    if (childWHRatio > parentWHRatio) {
      // Child should use 100% of the parent's width, and calculate the height based on its ratio
      result.cssWidth = '100%';
      result.width = parentWidth; // Since it's 100% of the parent's width
      // Using absolute value for the height:
      result.height = result.width / childWHRatio;
      result.cssHeight = result.height + 'px';
    } else {
      result.height = parentHeight; // Since it's 100% of the parent's height
      result.width = result.height * childWHRatio;
      if (this.alwaysUseWidthPct) {
        const childPct = 100 * result.width / parentWidth;
        result.cssWidth = childPct + '%';
        result.cssHeight = result.height + 'px';
      } else {
        result.cssWidth = result.width + 'px';
        // Child should use 100% of the parent's height, and calculate the width based on its ratio.
        // Code here is the inverse of the above.
        result.cssHeight = '100%';
      }
    }
    return result;
  }

  /**
   * Contain the entire child element within the parent by setting a percent width and 'auto' height.
   * @param {Element} child The html element which will be sized.
   * @param {int} logicalWidth For calculating the width/height ratio to be used for sizing the child element.
   * @param {int} logicalHeight For calculating the width/height ratio to be used for sizing the child element.
   * @param {double} childWHRatio If provided, this is the w/h ratio used and logicalWidth/-Height will be ignored.
   * @returns { width, height, cssWidth, cssHeight } Returns an object with width/height integers and
   * css width/height styles to be set on the child element (must be done by the caller).
   */
  containUsingWidthOnly(child, logicalWidth, logicalHeight, childWHRatio) {
    // If childWHRatio is specified, use that. Otherwise try to get the ratio from the child element:
    childWHRatio = childWHRatio || HtmlAspectRatioSizer.getWidthHeightRatio(child, logicalWidth, logicalHeight);
    const parent = child.parentNode;
    const parentWidth = parent.clientWidth;
    const parentHeight = parent.clientHeight;
    const parentWHRatio = parentWidth / parentHeight;
    const result = {};
    if (childWHRatio > parentWHRatio) {
      // Child should use 100% of the parent's width
      result.cssWidth = '100%';
      result.width = parentWidth;
      result.height = result.width / childWHRatio;
    } else {
      // Child should use 100% of the parent's height.
      result.height = parentHeight;
      result.width = result.height * childWHRatio;
      const childPct = 100 * result.width / parentWidth;
      result.cssWidth = childPct + '%';
    }
    result.cssHeight = 'auto';
    return result;
  }

  /**
   * Calculates the size of the child when sizing such that the entire child is placed within its
   * parent's client area while maintaining the child's width/height ratio.
   * @param {Element} child The element whose size we are calculating.
   * @param {int} logicalWidth For calculating the width/height ratio to be used for sizing the child element.
   * @param {int} logicalHeight For calculating the width/height ratio to be used for sizing the child element.
   * @param {double} childWHRatio If provided, this is the w/h ratio used and logicalWidth/-Height will be ignored.
   * @returns { width, height, cssWidth, cssHeight } Returns an object with width/height integers and
   * css width/height styles to be set on the child element (must be done by the caller).
   */
  getContainedChildSize(child, logicalWidth, logicalHeight, childWHRatio) {
    switch (this.sizerStrategy) {
      case HtmlSizerStrategy.pctWidthPxHeight:
        return this.containUsingWidthAndHeight(child, logicalWidth, logicalHeight, childWHRatio);
      case HtmlSizerStrategy.pctWidth:
        return this.containUsingWidthOnly(child, logicalWidth, logicalHeight, childWHRatio);
      default:
        return {};
    }
  }

  /**
   * Resizes the child element such that the entire child is placed within its parent's client
   * area while maintaining the child's width/height ratio.
   * @param {Element} child The element whose size we are calculating.
   * @param {double} childWHRatio If provided, this is the w/h ratio used (instead of the ratio
   * from the current size of the child element).
   */
  contain(child, childWHRatio) {
    const { cssWidth, cssHeight, width, height } = this.getContainedChildSize(
      child, child.clientWidth, child.clientHeight,  childWHRatio);
    child.style.width = cssWidth;
    child.style.height = cssHeight;
    this.childWidth = width;
    this.childHeight = height;
  }

  sizeToFullWidth(child, childWHRatio) {
    // If childWHRatio is specified, use that. Otherwise try to get the ratio from the child element:
    childWHRatio = childWHRatio || HtmlAspectRatioSizer.getWidthHeightRatio(child);
    const childWidth = child.parentNode.clientWidth;
    const childHeight = childWidth / childWHRatio;
    child.style.width = '100%';
    switch (this.sizerStrategy) {
      case HtmlSizerStrategy.pctWidthPxHeight: {
        child.style.height = childHeight + 'px';
        break;
      }
      case HtmlSizerStrategy.pctWidth: {
        child.style.height = 'auto';
        break;
      }
    }
    this.childHeight = childHeight;
    this.childWidth = childWidth;
  }

  sizeToFullHeight(child, childWHRatio) {
    // If childWHRatio is specified, use that. Otherwise try to get the ratio from the child element:
    childWHRatio = childWHRatio || HtmlAspectRatioSizer.getWidthHeightRatio(child);
    const childHeight = child.parentNode.clientHeight;
    const childWidth = childHeight * childWHRatio;
    const childWidthPct = 100 * childWidth / child.parentNode.clientWidth;
    child.style.width = childWidthPct + '%';
    switch (this.sizerStrategy) {
      case HtmlSizerStrategy.pctWidthPxHeight: {
        child.style.height = childHeight + 'px';
        break;
      }
      case HtmlSizerStrategy.pctWidth: {
        child.style.height = 'auto';
        break;
      }
    }
    this.childHeight = childHeight;
    this.childWidth = childWidth;
  }

  maximizeWithinParent(child, logicalWidth, logicalHeight) {
    const { cssWidth, cssHeight } = this.getContainedChildSize(child, logicalWidth, logicalHeight);
    child.style.width = cssWidth;
    child.style.height = cssHeight;
  }
}
